import { useQuery, useMutation } from '@apollo/client';
import {
  DropDown, Toaster, Button, Icon,
  ErrorMessage,
} from 'anf-core-react';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer, useState } from 'react';
import useTranslatedText from '../../hooks/useTranslatedText';
import { USER_PREFERENCES_ACTION } from '../../tools/constants';
import getUPrefValue from '../../tools/getUPrefValue';
import $window from '../../tools/window';
import { ERROR_MESSAGE } from '../Messages/Messages';
import style from './UserPreferencesToaster.module.scss';
import { USERCONFIG_DATA, USER_PREFERENCES_REQUEST } from './queries';
import getDropDownOptions from './tools/getDropDownOptions';
import { userPreferenceReducer, initialState } from './tools/userPreferenceReducer';

const UserPreferencesToaster = ({
  defaultCountry = '',
  handleToasterClose = () => {},
  isUserPreferencesToasterOpen = false,
}) => {
  const shipTo = useTranslatedText('shipto', { fallback: 'Ship To' });
  const regionalPreferences = useTranslatedText('regionalPreferences', {
    fallback: 'Regional Preferences',
  });
  const regionalPreferencesNote = useTranslatedText(
    'regionalPreferencesNote',
    {
      fallback:
        'Pricing, promotions, and product availability may vary by location. Items in your bag will be transferred based on availability.',
    },
  );
  const language = useTranslatedText('language', { fallback: 'Language' });
  const currency = useTranslatedText('currency', { fallback: 'Currency' });
  const updatePreferencesTmnt = useTranslatedText('updatePreferences', { fallback: 'Update Preferences' });
  const updatedTmnt = useTranslatedText('updated', { fallback: 'Updated' });
  const [submitStatus, setSubmitStatus] = useState({
    isLoading: false,
    isSuccess: false,
    isError: false,
  });

  const browserLanguageCode = $window?.navigator?.language || '';
  const hostName = $window?.location.hostname || '';
  const url = $window?.location.href || '';
  const [userPreferences, dispatch] = useReducer(userPreferenceReducer, {
    ...initialState,
    selectedCountryOption: defaultCountry,
  });

  const reloadPage = () => {
    $window.location.reload();
  };

  const {
    previousData, loading, error, data: userConfigData,
  } = useQuery(USERCONFIG_DATA, {
    variables: {
      countryCode: userPreferences.selectedCountryOption,
      languageCodeFromBrowser: browserLanguageCode,
      currencyCode: userPreferences.selectedCurrency,
    },
  });

  useEffect(() => {
    if (!userPreferences.hasFetched && userConfigData?.userConfig) {
      const {
        languages: fetchedLanguages,
        currencies: fetchedCurrencies,
      } = userConfigData.userConfig;

      const activeLanguage = fetchedLanguages.find((lang) => lang.isActive)?.languageCode || '';
      const activeCurrency = fetchedCurrencies.find((curr) => curr.isActive)?.code || '';

      dispatch({
        type: USER_PREFERENCES_ACTION.INITIALIZE,
        payload: {
          languages: fetchedLanguages,
          currencies: fetchedCurrencies,
          selectedLanguage: activeLanguage,
          selectedCurrency: getUPrefValue('cur') || activeCurrency,
        },
      });
    }
  }, [userConfigData, userPreferences.hasFetched]);

  const [updateUserPreferences] = useMutation(USER_PREFERENCES_REQUEST);

  const handleMutationError = (mutationError) => {
    setSubmitStatus({ isLoading: false, isSuccess: false, isError: true });
    console.error('Error:', mutationError);
  };

  const handleMutationComplete = (mutationData) => {
    setSubmitStatus({ isLoading: false, isSuccess: true, isError: false });
    const { updateUserPreferencesRequest } = mutationData.data;

    const redirectUrl = updateUserPreferencesRequest?.redirectingUrl;
    if (updateUserPreferencesRequest.language && redirectUrl) {
      setTimeout(() => {
        $window.location.href = redirectUrl;
      });
    } else {
      setTimeout(reloadPage);
    }
  };

  const handleCountryMutationComplete = (mutationData) => {
    setSubmitStatus({ isLoading: false, isSuccess: true, isError: false });
    const { updateUserPreferencesRequest } = mutationData.data;

    if (updateUserPreferencesRequest.url && updateUserPreferencesRequest.transferBag) {
      $window.location.href = updateUserPreferencesRequest.url;
    }

    if (
      updateUserPreferencesRequest.response.success
      || updateUserPreferencesRequest.refresh
    ) {
      setTimeout(reloadPage);
    }
  };

  const handleCountryChangeEvent = (event) => {
    const newCountry = event.target.value;
    dispatch({
      type: USER_PREFERENCES_ACTION.COUNTRY_CHANGE,
      payload: newCountry,
    });
  };

  const handleLanguageChangeEvent = (event) => {
    const newLanguageCode = event.target.value;
    dispatch({
      type: USER_PREFERENCES_ACTION.LANGUAGE_CHANGE,
      payload: newLanguageCode,
    });
  };

  const handleCurrencyChangeEvent = (event) => {
    const newCurrency = event.target.value;
    dispatch({
      type: USER_PREFERENCES_ACTION.CURRENCY_CHANGE,
      payload: newCurrency,
    });
  };

  const handleUpdatePreferencesClick = () => {
    setSubmitStatus({ isLoading: true, isSuccess: false, isError: false });
    const dropDownLanguage = userPreferences.isLanguageChanged
      ? userPreferences.languages.find(
        (languageObj) => languageObj.languageCode === userPreferences.selectedLanguage,
      )
      : null;

    const dataToSend = {
      ...(userPreferences.isCountryChanged && {
        countryCode: userPreferences.selectedCountryOption,
      }),
      ...(userPreferences.isLanguageChanged && {
        languageCode: userPreferences.selectedLanguage,
        languageId: dropDownLanguage.languageId,
        hostName,
        url,
      }),
      ...(userPreferences.isCurrencyChanged
        && { currencyCode: userPreferences.selectedCurrency }),
    };

    const isCountryOnlyUpdate = dataToSend.countryCode
      && !dataToSend.languageCode
      && !dataToSend.currencyCode;

    if (isCountryOnlyUpdate) {
      $window.dispatchEvent(new CustomEvent('mfe:resetFranchiseCountry'));
      updateUserPreferences({
        variables: { countryCode: dataToSend.countryCode },
      })
        .then(handleCountryMutationComplete)
        .catch(handleMutationError);
    } else {
      updateUserPreferences({ variables: dataToSend })
        .then(handleMutationComplete)
        .catch(handleMutationError);
    }
  };

  if (error) return <div>{ERROR_MESSAGE}</div>;
  if (loading && !previousData) return null;

  return (
    <Toaster
      closeButtonLabelKey=""
      direction="from-right"
      heading={(
        <div className="scope-1892">
          <span key="standard-header-span" className="h2">{regionalPreferences.value}</span>
        </div>
      )}
      id="user-preference-toaster"
      isOpen={isUserPreferencesToasterOpen}
      onClose={handleToasterClose}
    >
      <div className={`scope-1892 ${style.modalWrapper}`} data-testid="user-preference-menu">
        <span aria-level="2" id="user-preference-note" role="heading">
          {regionalPreferencesNote.value}
        </span>
        <DropDown
          id="country-option-dropdown"
          isDisabled={userPreferences.isLanguageChanged || userPreferences.isCurrencyChanged}
          label={shipTo.value}
          onChange={handleCountryChangeEvent}
          options={getDropDownOptions(userConfigData?.userConfig.countries || [], 'name', 'countryCode')}
          value={userPreferences.selectedCountryOption}
        />
        <DropDown
          id="language-option-dropdown"
          isDisabled={userPreferences.isCurrencyChanged || userPreferences.isCountryChanged}
          label={language.value}
          onChange={handleLanguageChangeEvent}
          options={getDropDownOptions(userPreferences.languages || [], 'name', 'languageCode')}
          value={userPreferences.selectedLanguage}
        />
        { userPreferences.currencies.length < 2 ? (
          <p>{`${currency.value}: ${userPreferences.selectedCurrency} (${userPreferences.currencies[0]?.code})`}</p>
        ) : (
          <DropDown
            id="currency-option-dropdown"
            isDisabled={userPreferences.isLanguageChanged || userPreferences.isCountryChanged}
            label={currency.value}
            onChange={handleCurrencyChangeEvent}
            options={getDropDownOptions(userPreferences.currencies || [], 'code', 'code')}
            value={userPreferences.selectedCurrency}
          />
        )}
        {submitStatus.isError ? <ErrorMessage>{ERROR_MESSAGE}</ErrorMessage> : null}
        <Button
          isDisabled={
          (!userPreferences.isCurrencyChanged
            && !userPreferences.isLanguageChanged
            && !userPreferences.isCountryChanged)
            || submitStatus.isError
          }
          isFullWidth
          isProcessing={submitStatus.isLoading}
          onClick={handleUpdatePreferencesClick}
          variant="secondary"
        >
          <span>
            {submitStatus.isSuccess ? (
              <>
                <Icon
                  icon="checkmark"
                  labelText="labelText-Check"
                  size="s"
                />
                {updatedTmnt.value}
              </>
            ) : (
              updatePreferencesTmnt.value
            )}
          </span>
        </Button>
      </div>
    </Toaster>
  );
};

UserPreferencesToaster.propTypes = {
  isUserPreferencesToasterOpen: PropTypes.bool,
  handleToasterClose: PropTypes.func,
  defaultCountry: PropTypes.string,
};

export default UserPreferencesToaster;
